/// One competitor service a firm OR a lawyer is currently using. Mirrors the
/// Rust `CustomerCompetitorServiceDto` returned by
/// `/api/firms/:firm_id/competitor-services` and
/// `/api/lawyers/:lawyer_id/competitor-services`.
///
/// `firm_id` / `lawyer_id` are mutually exclusive (XOR) — exactly one is set.
/// 事務所単位 (catalog No.43-45) → `firm_id` 入り、 弁護士単位 (catalog No.107-109)
/// → `lawyer_id` 入り。
export type CustomerCompetitorUsageStatus =
  | 'using'
  | 'past'
  | 'considering'
  | 'unknown';

export const CUSTOMER_COMPETITOR_USAGE_STATUS_LABELS: Record<
  CustomerCompetitorUsageStatus,
  string
> = {
  using: '利用中',
  past: '過去利用',
  considering: '検討中',
  unknown: '不明',
};

/// 新規登録 / 編集で選べる利用状況は `利用中` / `過去利用` の2択のみ
/// (spec 20260604-solution-usage-add-modal-spec)。 `検討中` / `不明` は
/// 既存行の表示フォールバック用に型・ラベルには残すが、保存導線からは外す。
export const CUSTOMER_COMPETITOR_USAGE_STATUS_OPTIONS = [
  { value: 'using', label: '利用中' },
  { value: 'past', label: '過去利用' },
] as const satisfies readonly {
  value: CustomerCompetitorUsageStatus;
  label: string;
}[];

export type CustomerCompetitorSwitchability =
  | 'high'
  | 'medium'
  | 'low'
  | 'unknown';

export const CUSTOMER_COMPETITOR_SWITCHABILITY_LABELS: Record<
  CustomerCompetitorSwitchability,
  string
> = {
  high: '高',
  medium: '中',
  low: '低',
  unknown: '不明',
};

export const CUSTOMER_COMPETITOR_SWITCHABILITY_OPTIONS = [
  { value: 'high', label: '高' },
  { value: 'medium', label: '中' },
  { value: 'low', label: '低' },
  { value: 'unknown', label: '不明' },
] as const satisfies readonly {
  value: CustomerCompetitorSwitchability;
  label: string;
}[];

export interface CustomerCompetitorService {
  id: string;
  firm_id: string | null;
  lawyer_id: string | null;
  /** competitor_services マスタへの FK。 */
  competitor_service_id: string;
  /** 表示用 (competitor_services.name の JOIN 結果)。 */
  competitor_service_name: string;
  usage_status: CustomerCompetitorUsageStatus;
  renewal_rejection_deadline: string | null;
  contract_end_date: string | null;
  memo: string | null;
  switchability: CustomerCompetitorSwitchability | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerCompetitorServiceList {
  items: CustomerCompetitorService[];
}

/// 作成リクエスト。 他社ソリューションは検索 + 候補選択で確定した既存マスターの
/// `competitor_service_id` で送る。 名前文字列での保存・自由入力からの get-or-create
/// は廃止 (spec 20260604-solution-usage-add-modal-spec)。 memo は通常 UI から送らない。
export interface CustomerCompetitorServiceCreateRequest {
  competitor_service_id: string;
  usage_status?: CustomerCompetitorUsageStatus;
  renewal_rejection_deadline?: string | null;
  contract_end_date?: string | null;
  switchability?: CustomerCompetitorSwitchability | null;
}

/// PATCH body. Date fields use the tri-state on the wire:
/// absent → keep, `null` → clear, "YYYY-MM-DD" → overwrite.
/// `competitor_service_id` を送ると参照先マスターを差し替える。 memo は送らない。
export interface CustomerCompetitorServiceUpdateRequest {
  competitor_service_id?: string;
  usage_status?: CustomerCompetitorUsageStatus;
  renewal_rejection_deadline?: string | null;
  contract_end_date?: string | null;
  switchability?: CustomerCompetitorSwitchability | null;
}

/// 競合サービス API の宛先。 事務所単位 / 弁護士単位 を XOR で表現する。
/// CompetitorServicesPanel と CompetitorServiceModal で共有。
export type CompetitorServiceTarget =
  | { kind: 'firm'; firmId: string }
  | { kind: 'lawyer'; lawyerId: string };
